import { BASE_URL } from '../../../shared/lib/api';

export type ViewTier = 'private' | 'tier1' | 'tier2' | 'tier3' | 'basic';
export type TimeWindow = '1m' | '3m' | '6m' | '1y' | '2y';

export interface DashboardMeta {
  as_of_date: string;
  refreshed_at: string;
  time_window: TimeWindow | 'all';
  refresh_needed: boolean;
  refresh_op_id: string | null;
  refresh_phase: 'bootstrap_private' | 'recalc' | 'final_private' | null;
  cache_built_at: string | null;
  served_from_cache: boolean;
  refresh_type?: 'same_day' | 'diff_day' | null;  // Optional for backward compat
  source?: 'redis' | 'on_demand' | 'case_d_step1';  // Optional for backward compat
  tier?: ViewTier;
  allowed?: boolean;
}

export interface DashboardResponse {
  data: any; // Keep existing shape for now
  meta: DashboardMeta;
}

const dashboardResponseCache = new Map<string, { etag: string; response: DashboardResponse }>();

async function fetchDashboardWithEtag(url: string, token: string, errorPrefix: string): Promise<DashboardResponse> {
  const cacheKey = `${url}|${token.slice(-16)}`;
  const cached = dashboardResponseCache.get(cacheKey);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  if (cached?.etag) {
    headers['If-None-Match'] = cached.etag;
  }

  const res = await fetch(url, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (res.status === 304 && cached) {
    return cached.response;
  }
  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (res.status === 403) {
    throw new Error('Not friends or insufficient tier');
  }
  if (!res.ok) {
    throw new Error(`${errorPrefix} failed: ${res.status}`);
  }

  const json = (await res.json()) as DashboardResponse;
  const etag = res.headers.get('ETag');
  if (etag) {
    dashboardResponseCache.set(cacheKey, { etag, response: json });
  }
  return json;
}

/**
 * Get self dashboard data from Redis
 */
export async function getSelfDashboard(params: {
  window: TimeWindow;
  tier: ViewTier;
  token: string;
}): Promise<DashboardResponse> {
  const queryParams = new URLSearchParams({ window: params.window });
  
  // Never send tier=basic to backend (frontend-only tier)
  if (params.tier && params.tier !== 'basic') {
    queryParams.append('tier', params.tier);
  }

  return fetchDashboardWithEtag(
    `${BASE_URL}/portfolio/dashboard?${queryParams.toString()}`,
    params.token,
    'getSelfDashboard'
  );
}

/**
 * Get friend dashboard data from Redis
 */
export async function getFriendDashboard(params: {
  friendId: string;
  window: TimeWindow;
  token: string;
}): Promise<DashboardResponse> {
  return fetchDashboardWithEtag(
    `${BASE_URL}/friends/${params.friendId}/portfolio?time_window=${params.window}`,
    params.token,
    'getFriendDashboard'
  );
}

export interface RebuildStatusResponse {
  private_rebuilding: boolean;
  started_at: string | null;
  op_id: string | null;
  cache_built_at: string | null;
}

/**
 * Get private cache rebuild status for toast synchronization
 */
export async function getPrivateCacheRebuildStatus(
  token: string
): Promise<RebuildStatusResponse> {
  const res = await fetch(`${BASE_URL}/portfolio/dashboard/rebuild-status`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    throw new Error(`getPrivateCacheRebuildStatus failed: ${res.status}`);
  }

  const json = (await res.json()) as RebuildStatusResponse;
  return json;
}
